/**
 * us-consent-calls.js: the theme side of the Super App Bounce Tracer contract,
 * WITHDRAW PATH ONLY.
 *
 * Contract: murphy-door-super-app docs/bounce-tracer/theme-contract.md, (c).
 * Ported from the Murphy Door theme's assets/md-consent-calls.js. The one
 * difference: it reads consent through window.usConsent (us-consent-gate.js)
 * instead of keeping a second consent reader, so it must load AFTER the gate
 * (both are deferred, and deferred scripts run in document order).
 *
 * ONE call, to the storefront-relative app proxy (Shopify signs it and strips
 * cookies), answered 204 whatever happens, never retried:
 *
 *  WITHDRAW  POST /apps/super-app/consent-withdraw  { visitorId }
 *     When analytics OR sale/share consent goes from allowed to not allowed on
 *     this page (a revoke through the banner or the "Do Not Sell or Share"
 *     preferences, or a decline after an earlier accept), send the md_vid once
 *     so the app tombstones the visitor and deletes their return-within-14-days
 *     rows. This is the PRIMARY deletion path (D1). Deleting is the safe
 *     direction, so a sale/share revoke counts too.
 *     It also clears the md_cx capture-evidence cookie at once. The Super App's
 *     Engagement Heartbeat embed owns md_cx and clears it on its next check;
 *     clearing it here too means the web pixel can never attach evidence after
 *     a revoke, even if the embed is off or has not run yet. Not a request.
 *
 * NOTHING ELSE. The banner-choice tally and its GPC outcome were retired
 * permanently (counsel decision 2026-10-10). No request is ever sent because
 * of a decline, a GPC opt-out, an accept or leaving without a choice. Do not
 * add one here.
 *
 * Request shape is fixed by the contract: fetch with keepalive and
 * credentials:'omit'. NEVER navigator.sendBeacon: it always sends cookies.
 *
 * Prior state: the first AUTHORITATIVE read (Shopify's Customer Privacy API
 * answered) on this page, through usConsent.onConsent. A page where the API
 * never answers has no prior, so it never sends. GPC forces sale/share to
 * false from the first read, so a GPC visitor never "transitions".
 *
 * Staff browsers (window.MD_INTERNAL_TRAFFIC === true) send nothing.
 * Everything is wrapped: a privacy call must never break the page.
 */
(function () {
  'use strict';

  var WITHDRAW_URL = '/apps/super-app/consent-withdraw';
  var UUID_V4 =
    /^[0-9a-f]{8}-[0-9a-f]{4}-4[0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

  function isStaff() {
    return window.MD_INTERNAL_TRAFFIC === true;
  }

  /* The one network primitive. Cookie-less by contract, fire-and-forget, no retry. */
  function post(url, body) {
    if (isStaff()) return false;
    try {
      if (typeof window.fetch !== 'function') return false;
      window
        .fetch(url, {
          method: 'POST',
          keepalive: true,
          credentials: 'omit',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(body),
        })
        .catch(function () {});
      return true;
    } catch (_) {
      return false;
    }
  }

  function readVisitorId() {
    try {
      var m = document.cookie.match(/(?:^|;\s*)md_vid=([^;]*)/);
      var v = m ? decodeURIComponent(m[1]) : '';
      // Only the pixel's UUID v4 is a real id. A sess_ nonce (or anything
      // else) is rejected by the server for every ID-keyed write, so skip it.
      return UUID_V4.test(v) ? v : '';
    } catch (_) {
      return '';
    }
  }

  /* Same Path/SameSite the Engagement Heartbeat embed writes it with (Secure
     on https, where the storefront always runs). */
  function clearEvidence() {
    try {
      if (!/(?:^|;\s*)md_cx=/.test(document.cookie)) return;
      var secure =
        window.location && window.location.protocol === 'https:'
          ? '; Secure'
          : '';
      document.cookie = 'md_cx=; Path=/; Max-Age=0; SameSite=Lax' + secure;
    } catch (_) {}
  }

  function sendWithdraw() {
    clearEvidence();
    var vid = readVisitorId();
    if (!vid) return false;
    return post(WITHDRAW_URL, { visitorId: vid });
  }

  var prior = null; // { analytics, sale } from the last authoritative read

  function onConsent(c) {
    try {
      if (isStaff() || !c || !c.isAuthoritative()) return;
      var next = {
        analytics: c.analyticsAllowed() === true,
        sale: c.saleOfDataAllowed() === true,
      };
      // Withdrawal: a real earlier grant changed to not-allowed on this page.
      if (
        prior &&
        ((prior.analytics && !next.analytics) || (prior.sale && !next.sale))
      ) {
        sendWithdraw();
      }
      prior = next;
    } catch (_) {}
  }

  try {
    if (isStaff()) return;
    var gate = window.usConsent;
    if (!gate || typeof gate.onConsent !== 'function') return; // no gate, no reader
    gate.onConsent(onConsent);
  } catch (_) {}
})();
